import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import type { UtmParameter } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';

interface BoostAppliedUtmProps {
  // The UTM pairs frozen at launch (configSnapshot.utmParameters).
  parameters: UtmParameter[];
  // The destination URL frozen at launch. Shown alongside the pairs so the brand
  // reads the exact link the ad pointed to, not just the tracking added to it.
  targetUrl?: string | null;
  // Overrides the block heading (the setup form labels it as the previous boost).
  title?: string;
}

/**
 * Read-only, compact list of the UTM parameters actually applied to a launched
 * boost's destination link, taken verbatim from the frozen config snapshot.
 * Renders nothing when the snapshot froze neither a destination nor any pair.
 */
export function BoostAppliedUtm({ parameters, targetUrl = null, title }: BoostAppliedUtmProps) {
  const { t } = useTranslation();

  if (parameters.length === 0 && !targetUrl) return null;

  return (
    <Box mt={5} border="1px solid" borderColor="border.muted" borderRadius="md" px={3} py={3}>
      <Text
        fontSize="2xs"
        fontWeight="medium"
        color="fg.muted"
        textTransform="uppercase"
        letterSpacing="wider"
        mb={2}
      >
        {title ?? t('mentions.boost.detail.utmTitle')}
      </Text>
      <VStack align="stretch" gap={1}>
        {targetUrl && (
          <HStack gap={2} align="baseline">
            <Text
              w="140px"
              flexShrink={0}
              fontFamily="mono"
              fontSize="xs"
              color="fg.muted"
              truncate
            >
              {t('mentions.boost.detail.utmDestinationLabel')}
            </Text>
            <Text flex={1} minW={0} fontFamily="mono" fontSize="xs" wordBreak="break-all">
              {targetUrl}
            </Text>
          </HStack>
        )}
        {parameters.map((pair) => (
          <HStack key={pair.key} gap={2} align="baseline">
            <Text
              w="140px"
              flexShrink={0}
              fontFamily="mono"
              fontSize="xs"
              color="fg.muted"
              truncate
            >
              {pair.key}
            </Text>
            <Text flex={1} minW={0} fontFamily="mono" fontSize="xs" wordBreak="break-all">
              {pair.value}
            </Text>
          </HStack>
        ))}
        {parameters.length === 0 && (
          <Text fontSize="xs" color="fg.muted">
            {t('mentions.boost.detail.utmNone')}
          </Text>
        )}
      </VStack>
    </Box>
  );
}
